import React, { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';
import ConfirmDialog from '../../../components/ConfirmDialog.jsx';

function formatPrice(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export default function SuperadminSalesRepsShow({ sales_rep, deals = [] }) {
  const { flash } = usePage().props;
  // Only ever present immediately after create or regenerate — the token is
  // hashed at rest, so it cannot be recovered on a later page load.
  const freshLoginUrl = sales_rep.login_url || flash?.fresh_login_url;

  const [copied, setCopied] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(freshLoginUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const regenerate = () => {
    setBusy(true);
    router.post(`/superadmin/sales_reps/${sales_rep.id}/regenerate_token`, {}, {
      onFinish: () => { setBusy(false); setConfirmRegenerate(false); },
    });
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link href="/superadmin/sales_reps" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            ← All sales reps
          </Link>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">{sales_rep.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{sales_rep.email} · added {sales_rep.created_at}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Portal access</h2>

          {freshLoginUrl ? (
            <>
              <div className="flex gap-2">
                <input readOnly value={freshLoginUrl}
                       className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-700" />
                <button type="button" onClick={copyLink}
                        className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer transition">
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Send this to {sales_rep.name}. It works once and expires in 30 minutes — after that they
                request their own link from the sales login page.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-600">
              The login link is hashed once issued, so it can't be shown again. {sales_rep.name} can request
              a fresh one at <span className="font-mono">/sales/login</span>, or you can regenerate one here.
            </p>
          )}

          <button type="button" onClick={() => setConfirmRegenerate(true)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
            Regenerate login link
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Deals created ({deals.length})</h2>
          {deals.length === 0 ? (
            <p className="text-sm text-gray-500">No deals yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {deals.map((deal) => (
                <li key={deal.id} className="py-2.5 flex items-center justify-between gap-3">
                  <Link href={`/superadmin/deals/${deal.id}`} className="text-sm font-medium text-gray-900 hover:text-indigo-700">
                    {deal.name}
                  </Link>
                  <span className="text-xs text-gray-500">
                    {formatPrice(deal.price_cents)} · {deal.status} · {deal.created_at}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmRegenerate}
        title="Regenerate the login link?"
        body="The rep's current link stops working immediately. If they're mid-session they stay signed in until they log out."
        confirmLabel="Regenerate"
        tone="primary"
        busy={busy}
        onConfirm={regenerate}
        onCancel={() => setConfirmRegenerate(false)}
      />
    </Layout>
  );
}
